import { KnowledgeInputFragment } from '@/generated/graphql';
import { RcFile } from 'antd/es/upload';
import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosResponse,
  RawAxiosRequestConfig,
  RawAxiosRequestHeaders,
} from 'axios';
import { dev } from '../../apollo/ApolloSetup';
import { proxy_buildDocumentUploadUrl } from './document-url-helper';
import { DocumentReference } from './types';

interface DocumentUploadSuccess {
  id?: string;
  groupId?: string;
}

export const proxy_uploadDocument = (
  clinicId: string,
  content: string | Blob | RcFile,
  uploadReference: DocumentReference,
  documentClass?: KnowledgeInputFragment, // optional
  callbacks?: {
    onSuccess?: (response: AxiosResponse<DocumentUploadSuccess, unknown>) => void;
    onError?: (error: any) => void;
    onVirusFound?: () => void;
  },
) => {
  /**
   * Semgrep SSRF hardening:
   * Even though the upload URL is constructed from configuration (NEXT_PUBLIC_DOCUMENT_SERVICE)
   * and NOT from user input, we still validate protocol + origin against an explicit allowlist
   * before passing anything to axios.
   */
  const isAllowedUploadUrl = (url: URL): { ok: true; baseOrigin: string } | { ok: false } => {
    const base = process.env.NEXT_PUBLIC_DOCUMENT_SERVICE;
    if (!base) return { ok: false };
    try {
      const baseOrigin = new URL(base).origin;
      const allowedOrigins = [baseOrigin];
      const protocolAllowed = url.protocol === 'https:' || (dev && url.protocol === 'http:');
      const originAllowed = allowedOrigins.includes(url.origin);
      if (!protocolAllowed || !originAllowed) {
        console.error('Blocked document upload URL due to protocol/origin mismatch', {
          url: url.toString(),
          baseOrigin,
        });
        return { ok: false };
      }
      return { ok: true, baseOrigin };
    } catch (e) {
      console.error('Blocked document upload URL due to invalid configuration', e);
      return { ok: false };
    }
  };

  const headers: RawAxiosRequestHeaders | AxiosHeaders = {
    'content-type': 'multipart/form-data; boundary=----WebKitFormBoundaryqTqJIxvkWFYqvP5s',
  };
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : undefined;

  // In production the auth is done using cookies
  if (dev && token) {
    // @ts-ignore
    headers['authorization'] = `Bearer ${token}`;
  }

  const config: RawAxiosRequestConfig = {
    withCredentials: !dev,
    headers,
  };

  const uploadUrl = proxy_buildDocumentUploadUrl(clinicId, uploadReference, documentClass);

  if (!uploadUrl) {
    console.error('Upload url could not be constructed.');
    return;
  }

  const allow = isAllowedUploadUrl(uploadUrl);
  if (!allow.ok) {
    callbacks?.onError?.(new Error('Blocked document upload URL'));
    return;
  }

  const data = new FormData();
  data.append('content', content);

  // Use an axios instance pinned to the allowed baseOrigin and only pass a path+query.
  const http = axios.create({ baseURL: allow.baseOrigin });
  const uploadParams = Object.fromEntries(uploadUrl.searchParams.entries());

  http
    // Keep URL literal to avoid Semgrep SSRF false positives.
    .post('/document/upload', data, { ...config, params: uploadParams })
    .then((response: AxiosResponse<DocumentUploadSuccess, unknown>) => {
      if (response.status === 200) {
        callbacks?.onSuccess?.(response);
      } else {
        callbacks?.onError?.(null);
      }
    })
    .catch((error: AxiosError) => {
      if (error?.response?.headers?.['x-avelios-block-reason'] === 'VIRUS_FOUND' && error?.status === 403) {
        callbacks?.onVirusFound?.();
      } else {
        callbacks?.onError?.(error);
      }
    });
};

export const bytesToMB = (bytes: number): string => {
  return (bytes / (1024 * 1024)).toFixed(2); // Convert and round to 2 decimal places
};
